import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getUserStats } from "@/utils/api";

interface MenuItem {
  path: string;
  label: string;
  icon: string;
  vipOnly?: boolean;
  statKey?: 'listings_count' | 'favorites_count' | 'history_count' | 'orders_count';
}

const menuItems: MenuItem[] = [
  { path: "/dashboard", label: "会员中心", icon: "ri-dashboard-line" },
  { path: "/dashboard/profile", label: "个人资料", icon: "ri-user-settings-line" },
  { path: "/dashboard/my-listings", label: "我的发布", icon: "ri-file-list-line", statKey: 'listings_count' },
  { path: "/dashboard/favorites", label: "我的收藏", icon: "ri-heart-line", statKey: 'favorites_count' },
  { path: "/dashboard/history", label: "浏览记录", icon: "ri-history-line", statKey: 'history_count' },
  { path: "/dashboard/payment-records", label: "付款记录", icon: "ri-receipt-line", statKey: 'orders_count' },
  { path: "/vip", label: "开通VIP", icon: "ri-vip-crown-line", vipOnly: true },
];

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { siteConfig } = useSiteConfig();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // VIP 从公开站点配置读取
  const enableVip = siteConfig?.enable_vip ?? true;
  const [stats, setStats] = useState<Record<string, number>>({});

  useEffect(() => {
    // 获取用户统计
    getUserStats()
      .then((data) => {
        setStats(data);
      })
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // 根据设置过滤菜单项
  const visibleMenuItems = menuItems.filter(item => {
    if (item.vipOnly && !enableVip) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navbar />
      <main className="pt-20">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Sidebar */}
              <aside className="lg:w-64 shrink-0">
                {/* Mobile Menu Toggle */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="lg:hidden w-full flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100 mb-4"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={user?.avatar}
                      alt={user?.nickname}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {user?.nickname}
                      </p>
                      <p className="text-xs text-gray-400">{user?.email}</p>
                    </div>
                  </div>
                  <i
                    className={`ri-${
                      isMobileMenuOpen ? "close" : "menu"
                    }-line text-xl text-gray-500`}
                  />
                </button>

                <div
                  className={`${
                    isMobileMenuOpen ? "block" : "hidden"
                  } lg:block bg-white rounded-2xl border border-gray-100 overflow-hidden`}
                >
                  {/* User Info */}
                  <div className="p-4 md:p-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <img
                        src={user?.avatar}
                        alt={user?.nickname}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-800 truncate">
                          {user?.nickname}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {user?.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Menu */}
                  <nav className="p-2">
                    {visibleMenuItems.map((item) => {
                      const isActive = location.pathname === item.path;
                      const count = item.statKey ? stats[item.statKey] : undefined;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                            isActive
                              ? "bg-orange-50 text-orange-600 font-medium"
                              : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span className="flex items-center gap-3">
                            <i className={`${item.icon} text-lg`} />
                            {item.label}
                          </span>
                          {count !== undefined && count > 0 && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                              isActive ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {count}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </nav>

                  {/* Logout */}
                  <div className="p-2 border-t border-gray-100">
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors w-full"
                    >
                      <i className="ri-logout-box-line text-lg" />
                      退出登录
                    </button>
                  </div>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                <div className="bg-white rounded-2xl border border-gray-100 min-h-[500px]">
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}